import moment from "moment";

import ChartActions from "store/actions/charts";
import { updateNewlyAddedLayout } from "store/actions/layout";

import { getAlarmsHistory } from "api/alarmsHistory";
import ChartAPIs from "api/chart";

import getLogger from "utils/logger.util";
import { capitalizeFirstLetter, extractLayout, getFiltersPayload, getGraphDataPointsInterval } from "utils/utils";
import ChartConstant from "constants/chartConstant";
import { TAG_TYPES } from "constants/globalConstants";
import { SYSTEMS_PAGES } from "constants/pages.constant";

import { setPageLayouts } from "./layout";

const PAGE = capitalizeFirstLetter(SYSTEMS_PAGES.CHARTS.slice(0, -1));

let activeAlarms = [];

const Logger = getLogger("Chart - Thunk");

export const getChartLayouts = findCurrentLayout => async dispatch => {
  try {
    dispatch(ChartActions.setChartLoading(true));
    dispatch(ChartActions.resetChartTagsData());
    const response = await ChartAPIs.getChart(null);
    const { Layouts } = response.data;
    if (Layouts.length) {
      const { defaultLayout, layouts } = findCurrentLayout(Layouts);
      dispatch(setPageLayouts(layouts, defaultLayout, SYSTEMS_PAGES.CHARTS));
    } else {
      dispatch(ChartActions.setChartLoading(false));
      dispatch(ChartActions.setChartTagDataLoading(false));
    }
  } catch (error) {
    Logger.error("getChartLayouts - Error while fetching layouts:", error);
    dispatch(ChartActions.setChartLoading(false));
    dispatch(ChartActions.setChartTagDataLoading(false));
  }
};

const getWidgetPayload = (LayoutID, Attributes) => ({ Type: PAGE, LayoutID, Attributes });

export const addDefaultWidget = layout => async (dispatch, getState) => {
  try {
    dispatch(ChartActions.resetChartTagsData());
    dispatch(updateNewlyAddedLayout(SYSTEMS_PAGES.CHARTS, layout));
    const { ID } = layout;
    const attr = {
      Duration: ChartConstant.CHART_DURATIONS["Past 1 day"],
      Format: ChartConstant.CHART_PLOT_FORMATS.SINGLE_PLOT
    };
    const payload = {
      Header: "Chart Layout Widget",
      ...getWidgetPayload(ID, attr)
    };
    const respData = await ChartAPIs.addChartWidget(payload);
    const { Id, Attributes } = respData;
    const Duration = getChartDuration(Attributes.Duration);
    const DurationRanges = getDurationRanges(Attributes.Duration, getState().chartData);
    const NormalizationFilters = parseNormalizedFilters(Attributes);
    const { Format, Tags, Alarms } = Attributes;
    const NormalizedTags = [];
    dispatch(ChartActions.chartDataFetched({ Id, Format, Duration, Tags, NormalizedTags, Alarms, DurationRanges, NormalizationFilters }));
  } catch (error) {
    Logger.error("Error while adding default widget: ", error);
  } finally {
    dispatch(ChartActions.setChartTagDataLoading(false));
  }
};

export const editChartWidget = data => async (dispatch, getState) => {
  dispatch(ChartActions.setChartTagDataLoading(true));
  const LayoutID = extractLayout();
  let { Id, Tags, Duration } = getState().chartData;
  const payload = getWidgetPayload(LayoutID, data);
  ChartAPIs.updateChartWidget({ Id, ...payload }); // Calling API in background
  const { Format, Duration: NewDuration } = data;
  if (Format) {
    dispatch(ChartActions.updateChartFormat(Format));
    dispatch(ChartActions.setChartTagDataLoading(false));
    dispatch(ChartActions.onPlotZoom({ isReset: true }));
  } else if (NewDuration) {
    if (NewDuration === ChartConstant.CHART_DURATIONS.Custom) {
      const DurationRanges = {
        startTime: data.StartDate,
        endTime: data.EndDate
      };
      dispatch(ChartActions.setChartDurationRanges(DurationRanges));
    }
    dispatch(ChartActions.resetTagsData());
    const duration = getChartDuration(NewDuration);
    dispatch(ChartActions.updateChartDuration(duration));
    Duration = NewDuration;
    // Fetch Tags Data after Header Update
    getTagsData({ Duration, Tags }, dispatch, getState);
    const ranges = getState().chartData.DurationRanges;
    dispatch(fetchAlarmsHistory(ranges));
  }
};

const isDurationChanges = (defaultDuration, newDuration) =>
  defaultDuration?.startTime !== newDuration?.startTime && defaultDuration?.endTime !== newDuration?.endTime;

export const fetchDataOnZoom = (zoomData, newGroup) => async (dispatch, getState) => {
  const { Tags, NormalizedTags, DurationRanges, NormalizationFilters, PlotZoomData } = getState().chartData;
  const getTimestamp = index => moment(zoomData.xRange[index]).unix();
  const newDuration = { startTime: getTimestamp(0), endTime: getTimestamp(1) };
  if ((!PlotZoomData?.group || PlotZoomData?.group > 10) && isDurationChanges(DurationRanges, newDuration)) {
    dispatch(ChartActions.setChartTagDataLoading(true));
    const baselineId = NormalizedTags.length ? NormalizationFilters.Baseline : null;
    const filtersParms = getFiltersPayload(NormalizationFilters);
    if (Tags?.length) {
      try {
        let params = getFetchTagsPayload(Tags, newDuration, newGroup);
        if (baselineId) {
          params = { ...params, ...filtersParms };
        }
        const resp = await ChartAPIs.getChartTimeSeriesData(params, baselineId);
        const respData = resp.data;
        dispatch(ChartActions.setPlotData({ respData }));
      } catch (err) {
        dispatch(ChartActions.setChartTagDataLoading(false));
        Logger.error("Get Tags Data Error: ", err);
      }
    } else {
      dispatch(ChartActions.setChartTagDataLoading(false));
    }
  }
};

export const addTagInWidget = (tagData, baselineId) => async (dispatch, getState) => {
  dispatch(ChartActions.setChartTagDataLoading(true));
  try {
    const WigdetID = getState().chartData.Id;
    const payload = { WigdetID, ...tagData };
    //Add New Tag
    await ChartAPIs.addChartTag(payload);
    const { tagsAsObj } = getState().tags;
    const tagDetail = getTagDetails(tagData, tagsAsObj);
    //Fetch Added Tag Data
    const { DurationRanges, TagsToShow, Format, NormalizationFilters } = getState().chartData;
    let params = getFetchTagsPayload([tagData], DurationRanges);
    params.includeNullPoints = true;
    const filtersParms = getFiltersPayload(NormalizationFilters);
    if (baselineId) {
      params = { ...params, ...filtersParms };
    }

    const updatedTagsInDb = [...getState().chartData.TagsInDb, tagDetail];
    dispatch(ChartActions.tagsLimits(updatedTagsInDb));

    const { data } = await ChartAPIs.getChartTimeSeriesData(params, baselineId);
    const { Time, normalized_baseline_calc, ...tagsData } = data;

    dispatch(checkTagsWithoutData(tagsData));
    dispatch(ChartActions.addedNewTag(tagDetail));
    dispatch(ChartActions.updatePlotData({ type: "ADD", data }));
    if (Format === ChartConstant.CHART_PLOT_FORMATS.MULTI_Y_PLOT && TagsToShow.length >= 5) {
      dispatch(editChartWidget({ Format: ChartConstant.CHART_PLOT_FORMATS.SINGLE_PLOT }));
    }
    //if 1st Added tag don't have data, then hiding loader
    const addedTags = getState().chartData.Tags;
    if (addedTags.length === 1) {
      const tagId = addedTags[0].TagId;
      if (!data[tagId].length) {
        dispatch(ChartActions.setChartTagDataLoading(false));
      }
    }
  } catch (err) {
    Logger.error("Unable to add Add Tag..", err);
    dispatch(ChartActions.setChartTagDataLoading(false));
  }
};

const getChartDuration = timeWindow => {
  const { CHART_DURATIONS } = ChartConstant;
  return Object.keys(CHART_DURATIONS).find(key => CHART_DURATIONS[key] === timeWindow);
};

export const getDurationRanges = (duration, chartData) => {
  if (duration === ChartConstant.CHART_DURATIONS.Custom) {
    const { DurationRanges } = chartData;
    return DurationRanges;
  } else {
    const dateDifferenceInMinutes = ChartConstant.ChartRangeInHrs[duration] * 60;
    const endDate = moment(new Date()).add(-dateDifferenceInMinutes, "m").toDate();
    const durationRanges = {
      startTime: moment(endDate).unix(),
      endTime: moment().unix()
    };
    return durationRanges;
  }
};

const getChartFormat = (chartFormat, tagCount) => {
  let plotFormat = chartFormat;
  const { CHART_PLOT_FORMATS } = ChartConstant;
  if ((tagCount === 1 && chartFormat !== CHART_PLOT_FORMATS.SINGLE_PLOT) || (chartFormat === CHART_PLOT_FORMATS.MULTI_Y_PLOT && tagCount > 5)) {
    plotFormat = CHART_PLOT_FORMATS.SINGLE_PLOT;
  }
  return plotFormat;
};

const checkTagsWithoutData = tagsData => async (dispatch, getState) => {
  const tagsWithoutData = [];
  const { tagsAsObj } = getState().tags;
  for (const TagId in tagsData) {
    const tagData = tagsData[TagId];
    const isTagHasData = Boolean(tagData.length) && tagData.some(val => val >= 0);
    if (!isTagHasData) {
      const { TagName } = getTagDetails({ TagId }, tagsAsObj);
      tagsWithoutData.push({ TagId, TagName });
    }
  }
  dispatch(ChartActions.updateTagsWithoutData(tagsWithoutData));
  if (tagsWithoutData.length === Object.keys(tagsData).length) {
    dispatch(ChartActions.setChartTagDataLoading(false));
  }
};

export const getFetchTagsPayload = (Tags, DurationRanges, group) => {
  const tags = Tags.map(tag => tag.TagId);
  group = group || getGraphDataPointsInterval(DurationRanges);
  group = Math.round(group / 10) * 10;
  return { tags, ...DurationRanges, group };
};

const fetchTagsData = async (Tags, Duration, DurationRanges, dispatch, baselineId, filtersParms, TagsToShow, isResetZoom) => {
  if (Tags.length) {
    try {
      let params = getFetchTagsPayload(Tags, DurationRanges);
      params.includeNullPoints = true;
      if (baselineId) {
        params = { ...params, ...filtersParms };
      }

      const resp = await ChartAPIs.getChartTimeSeriesData(params, baselineId);
      const respData = resp.data;
      const { Time, normalized_baseline_calc, ...tagsData } = respData;
      dispatch(ChartActions.setPlotData({ respData, isResetZoom }));
      dispatch(checkTagsWithoutData(tagsData));
      if (TagsToShow && TagsToShow.length === 0) {
        dispatch(ChartActions.setChartTagDataLoading(false));
      }
    } catch (err) {
      dispatch(ChartActions.setChartTagDataLoading(false));
      Logger.log("Get Tags Data Error: ", err);
    } finally {
      dispatch(ChartActions.setChartTagDataLoading(false));
    }
  } else {
    dispatch(ChartActions.setChartTagDataLoading(false));
  }
};

const getTagsData = async (Attributes, dispatch, getState) => {
  dispatch(ChartActions.setChartTagDataLoading(true));
  const { chartData } = getState();
  const { Duration, Tags, StartDate, EndDate } = Attributes;
  // const standardTags = Tags.filter(({ isNormalizedTag }) => !isNormalizedTag);
  let durationRanges = {};
  if (Duration === ChartConstant.CHART_DURATIONS.Custom && StartDate && EndDate) {
    durationRanges = { startTime: StartDate, endTime: EndDate };
  } else {
    durationRanges = getDurationRanges(Duration, chartData);
  }
  const { NormalizedTags, NormalizationFilters, TagsToShow } = chartData;
  dispatch(ChartActions.setChartDurationRanges(durationRanges));
  const baselineId = NormalizedTags.length ? NormalizationFilters.Baseline : null;
  const filtersParms = getFiltersPayload(NormalizationFilters);
  fetchTagsData(Tags, Duration, durationRanges, dispatch, baselineId, filtersParms, TagsToShow);
};

const parseNormalizedFilters = data => {
  const { Baseline, FeedFlowH, FeedFlowL, RecoveryH, RecoveryL, RejectConductivityH, RejectConductivityL } = data;
  return { Baseline, FeedFlowH, FeedFlowL, RecoveryH, RecoveryL, RejectConductivityH, RejectConductivityL };
};

const getTagDetails = (tag, tagsAsObj) => {
  const id = tag.TagId;
  const { TagName, Units, Type: TagType, Description } = tagsAsObj[id];
  const isNormalizedTag = TagType === TAG_TYPES.NORMALIZED;
  const { TagId, Color, Type, Show, Trendline, Ylower, Yupper } = tag;
  return { TagId, TagName, Units, Description, Color, Type, Show, Trendline, Ylower, Yupper, isNormalizedTag };
};

export const fetchChartData = currentLayout => async (dispatch, getState) => {
  try {
    dispatch(ChartActions.resetChartTagsData());
    const response = await ChartAPIs.getChart(currentLayout);
    const { Widgets } = response.data;
    activeAlarms = [];
    if (Widgets.length) {
      const { tagsAsObj } = getState().tags;
      const { Id, Attributes } = Widgets[0];
      const TagsInDb = Widgets[0].Attributes.Tags;
      let showTagsCount = 0;
      const Tags = [];
      const NormalizedTags = [];
      Attributes.Tags.forEach(tag => {
        const addedTag = getTagDetails(tag, tagsAsObj);
        if (addedTag.isNormalizedTag) NormalizedTags.push(addedTag);
        Tags.push(addedTag);
        if (tag.Show) showTagsCount++;
      });
      const Format = getChartFormat(Attributes.Format, showTagsCount);
      const Duration = getChartDuration(Attributes.Duration);
      const NormalizationFilters = parseNormalizedFilters(Attributes);
      const DurationRanges = getDurationRanges(Attributes.Duration, getState().chartData);
      const { Alarms } = Attributes;
      activeAlarms = Alarms;
      dispatch(ChartActions.chartDataFetched({ Id, Format, Duration, Tags, Alarms, DurationRanges, NormalizationFilters, NormalizedTags, TagsInDb }));
      getTagsData({ ...Attributes, Tags, NormalizedTags }, dispatch, getState);
      dispatch(fetchAlarmsHistory(DurationRanges));
    } else {
      dispatch(ChartActions.setChartTagDataLoading(false));
    }
  } catch (error) {
    Logger.error("Error while fetching tags data: ", error);
    dispatch(ChartActions.setChartTagDataLoading(false));
  } finally {
    dispatch(ChartActions.setChartLoading(false));
  }
};

export const fetchNormalizedTags = () => async (dispatch, getState) => {
  const { AllTags } = getState().chartData;
  const { tagsAsObj } = getState().tags;
  const ChartTagsCount = AllTags.length;
  const SystemTags = Object.values(tagsAsObj);
  if (ChartTagsCount === 0 || ChartTagsCount < SystemTags) {
    dispatch(ChartActions.setChartTags(SystemTags));
  }
};

export const editTagsView =
  ({ isOpened, updatedTags }) =>
  async (dispatch, getState) => {
    try {
      const { TagsInDb } = getState().chartData;
      if (updatedTags) {
        const Tags = [];
        updatedTags.forEach(tag => {
          if (tag.isEdited) {
            const { TagId, Color, Type, Show, Bin, Trendline, Ylower, Yupper } = tag;
            Tags.push({ TagId, Color, Type, Show, Bin, Trendline, Ylower, Yupper });
          }
        });
        const LayoutID = extractLayout();
        const { Id } = getState().chartData;
        const payload = getWidgetPayload(LayoutID, { Tags });
        const updatedTagsInDb = TagsInDb.map(originalTag => {
          const matchingUpdatedTag = Tags.find(t => t.TagId === originalTag.TagId);
          return matchingUpdatedTag ? matchingUpdatedTag : originalTag;
        });
        dispatch(ChartActions.tagsLimits(updatedTagsInDb));
        ChartAPIs.updateChartWidget({ Id, ...payload });
      }
      dispatch(ChartActions.updateEditedTags({ isOpened, updatedTags }));
    } catch (error) {
      Logger.error("editTagsView - Error while updating tags ", error);
    }
  };

export const onPreviewChange = Tags => async (dispatch, getState) => {
  const { Format } = getState().chartData;
  const tagsToShow = Tags.filter(tag => tag.Show);
  if (Format === ChartConstant.CHART_PLOT_FORMATS.MULTI_Y_PLOT && tagsToShow.length > 5) {
    dispatch(ChartActions.chartDataFetched({ Format: ChartConstant.CHART_PLOT_FORMATS.SINGLE_PLOT, Tags }));
  } else dispatch(ChartActions.chartDataFetched({ Tags }));
};

/**
 * This function used to refresh Plot Data. It will refresh data based on
 * current chart duration and all added tags.
 * This will fetch data for all tags irrespective of tag is shown or not.
 * Duration ranges will be calculated for current timestamp.
 *
 * @returns void
 */
export const refreshPlotData = isLiveTimerRunning => async (dispatch, getState) => {
  if (!isLiveTimerRunning) dispatch(ChartActions.setChartTagDataLoading(true));
  const { Tags, TagsToShow, Duration, NormalizedTags, NormalizationFilters } = getState().chartData;
  const duration = ChartConstant.CHART_DURATIONS[Duration];
  const DurationRanges = getDurationRanges(duration, getState().chartData);
  dispatch(ChartActions.setChartDurationRanges(DurationRanges));
  const baselineId = NormalizedTags.length ? NormalizationFilters.Baseline : null;
  const filtersParms = getFiltersPayload(NormalizationFilters);
  fetchTagsData(Tags, duration, DurationRanges, dispatch, baselineId, filtersParms, TagsToShow, true);
};

/**
 * This function is used to delete chart tagd from graph.
 * It will delete tag on server and then will update UI.
 *
 * @param {*} data : Tag Data which needs to delete
 * @returns void
 */
export const deleteChartTag = data => async (dispatch, getState) => {
  try {
    dispatch(ChartActions.setChartTagDataLoading(true));
    const { Id, Tags, Format, TagsInDb } = getState().chartData;
    const LayoutId = extractLayout();
    const { TagId } = data;
    const resp = await ChartAPIs.deleteChartTag(LayoutId, Id, TagId);
    if (resp && resp.status === 200) {
      if (Tags.length - 1 === 1 && Format !== ChartConstant.CHART_PLOT_FORMATS.SINGLE_PLOT) {
        // Only Single tag present in chart, change plot format to single, if its not SINGLE
        dispatch(editChartWidget({ Format: ChartConstant.CHART_PLOT_FORMATS.SINGLE_PLOT }));
      }
      dispatch(ChartActions.deletedTag(TagId));
      const updatedTagsInDb = TagsInDb.filter(tag => tag.TagId !== TagId);
      dispatch(ChartActions.tagsLimits(updatedTagsInDb));
    }
  } catch (error) {
    Logger.error("Error while deleting chart tag: ", error);
  } finally {
    dispatch(ChartActions.setChartTagDataLoading(false));
  }
};

export const fetchAlarmsHistory = DurationRanges => async dispatch => {
  try {
    const { data } = await getAlarmsHistory(DurationRanges);
    const alarmsHistory = data.Alarms.map(alarm => ({ ...alarm, Enable: activeAlarms.includes(alarm.AlarmId) }));
    dispatch(ChartActions.setAlarmsHistory(alarmsHistory));
  } catch (err) {
    Logger.error("Error while fethcing alarm history: ", err);
  }
};

export const editChartAlarms = (data, updatedAlarms) => async (dispatch, getState) => {
  const LayoutID = extractLayout();
  const { Id } = getState().chartData;
  const payload = getWidgetPayload(LayoutID, { Alarms: updatedAlarms });
  dispatch(ChartActions.updateAlarms({ updatedAlarms: data.Alarms, isClosedView: true }));
  ChartAPIs.updateChartWidget({ Id, ...payload }); // Calling API in background
};

export const updateChartFilters = (filterData, tagData) => async (dispatch, getState) => {
  if (filterData) {
    dispatch(ChartActions.setChartTagDataLoading(true));
    const LayoutID = extractLayout();
    const { chartData } = getState();
    const payload = getWidgetPayload(LayoutID, filterData);
    const response = await ChartAPIs.updateChartWidget({ Id: chartData.Id, ...payload });
    dispatch(ChartActions.updateNormalizeFilters(filterData));
    if (tagData) {
      dispatch(addTagInWidget(tagData, filterData.Baseline));
    } else {
      const { Duration, Tags, TagsToShow, DurationRanges } = chartData;
      const NormalizationFilters = { ...chartData.NormalizationFilters, ...filterData };
      const { Baseline } = response.Attributes;
      const baselineId = filterData.Baseline || Baseline;
      const filtersParms = getFiltersPayload(NormalizationFilters);
      dispatch(ChartActions.resetTagsData());
      fetchTagsData(Tags, Duration, DurationRanges, dispatch, baselineId, filtersParms, TagsToShow, true);
    }
  }
};
